// ============================================================================
// LE MODÈLE DE L'ÉCRAN « PARAMÈTRES » — relevé de la maquette, complété du catalogue.
//
// ⚠️ RELEVÉ, PAS INVENTÉ (correction 2026-09-06). La première version de cet écran prenait les
// onglets de la maquette puis improvisait le reste : liste plate, tout en champ texte, deux badges
// de mon cru. L'écart était « lunaire » (mot de l'utilisateur), et il était évitable —
// `scripts/extract-maquette-screen.mjs` existait déjà et rend la structure exacte d'un écran. Ce
// fichier porte ce relevé : sections titrées, forme de chaque réglage, gouvernance.
//
// ⚠️ LA MAQUETTE EST UNE ILLUSTRATION, PAS UN INVENTAIRE : elle dessine 46 réglages quand le
// catalogue en compte 69 (hors fidélité). On garde donc SON DÉCOUPAGE et on y verse TOUT le
// catalogue — un réglage qu'elle n'a pas dessiné existe quand même, et doit être pilotable. La garde
// `parametres-modele.test.ts` vérifie qu'aucun ne reste orphelin.
//
// ⚠️ TROIS GOUVERNANCES, relevées de `setRows()` — mais la première est RENOMMÉE. La maquette dit
// « Imposé Orgazone » ; le libellé retenu est « **Verrouillé** » (décision utilisateur 2026-09-06) :
// ce qui compte pour l'exploitant est qu'il ne peut pas y toucher, pas de savoir qui l'a décidé.
//   · `verrouille`  — l'éditeur le fixe (certification NF525, règles de conservation). Lecture seule.
//   · `bo`          — réglable ici, et ici seulement.
//   · `caisse`      — réglable ici ET en caisse par un administrateur ; dernier écrivain gagne.
//
// ⚠️ LA FORME VIENT DU CONTRAT, pas d'une devinette sur le nom : `toggle` est réservé aux interrupteurs
// que déclare `SWITCH_DEFAULTS` (`@orgazone/sync-contract`) — ceux que la caisse lit comme un booléen —,
// et le compilateur le tient dans les deux sens. Un faux booléen afficherait un interrupteur sur une
// valeur libre et l'écrirait « 0 » ou « 1 » à la première bascule ; un vrai booléen rangé en valeur
// s'afficherait en pastille « 1 », ou « défaut de la caisse » sans dire lequel.
// ============================================================================
import type { IconName } from '@orgazone/ui-tokens';
import {
  NEPTING_TERMINALS_SETTING, parseVocabularySettingKey, STORE_EMAIL_SETTING, STORE_PHONE_SETTING, STORE_SYNC_MODE_SETTING, type SwitchName,
} from '@orgazone/sync-contract';
import { key, type Translatable } from '../i18n';
// ⚠️ Le registre des intégrations est LU ici (les réglages qu'un connecteur commande sont écartés des
//    onglets) ; lui n'importe de ce modèle qu'un TYPE — pas de boucle au chargement.
import { CONNECTEURS } from '../integrations';

/**
 * La forme d'un réglage à l'écran. `toggle` = un interrupteur du contrat (`'1'`/`'0'`, défaut déclaré) ; `telephone` et
 * `email` = une saisie CONTRÔLÉE, dont le message sous le champ dit ce qui sera enregistré ou ce qui cloche.
 */
export type FormeReglage = 'toggle' | 'valeur' | 'secret' | 'liste' | 'telephone' | 'email';

/**
 * D'OÙ VIENNENT LES CHOIX D'UN RÉGLAGE EN FORME DE LISTE.
 *
 * ⚠️ 🔴 UNE LISTE SERVIE PAR UN TIERS, PAS UNE CONSTANTE. Un gabarit de coupon est identifié chez le
 * FOURNISSEUR : son identifiant ne se tape pas — une coquille donnerait un envoi refusé, un par un,
 * sans que personne ne sache pourquoi. L'écran demande donc la liste, et n'offre que ce qu'elle rend.
 */
export type SourceListe = 'wallet_coupon_templates';

interface ReglageBase {
  /** Le nom au catalogue (`@orgazone/sync-contract`) — c'est lui qui voyage. */
  name: string;
  /** Le libellé HUMAIN, relevé de la maquette quand elle le donne. */
  label: Translatable;
  /** Valeur technique (SIRET, n° de certificat) : rendue en chasse fixe, comme la maquette. */
  mono?: boolean;
}

/**
 * ⚠️ UNE UNION, PAS UN CHAMP OPTIONNEL : une forme `liste` DOIT dire quelle liste demander, sinon la
 * ligne retomberait en silence sur une pastille de texte ; un `toggle` DOIT nommer un interrupteur du
 * contrat, sinon son état au repos serait un défaut que personne n'applique. Un réglage déclaré par un
 * connecteur (`src/integrations.ts`) s'écrit en littéral, et c'est le compilateur qui tient la paire.
 */
export type ReglageVue =
  | (ReglageBase & { forme: 'liste'; source: SourceListe })
  | (ReglageBase & { forme: 'toggle'; name: SwitchName; source?: never })
  | (ReglageBase & { forme: Exclude<FormeReglage, 'liste' | 'toggle'>; source?: never });

/**
 * UN RÉGLAGE RANGÉ DANS PARAMÈTRES — jamais une liste servie par un tiers.
 * ⚠️ FERMÉ PAR LE TYPE : les seules listes (les gabarits de coupon wallet) vivent avec leur connecteur, qui les
 *    rend dans SA carte ; la ligne de Paramètres ne sait pas les dessiner, et le compilateur refuse qu'on en
 *    range une ici — elle retomberait en silence sur une pastille de texte.
 */
export type ReglageParam = Exclude<ReglageVue, { forme: 'liste' }>;

export interface SectionParam {
  titre: Translatable;
  /** Ce que la section règle — relevé de la maquette (`SETDATA[].d`). */
  note: Translatable;
  reglages: ReglageParam[];
}

/**
 * UN BLOC QU'UN ONGLET PORTE EN PLUS DE SES RÉGLAGES — liste FERMÉE.
 *
 * ⚠️ DÉCISION UTILISATEUR (2026-10-03) : le parc de périphériques et la messagerie du magasin sont des réglages DU
 *    MAGASIN — ils vivent dans Paramètres, pas dans des entrées de rail à part. Leur forme ne tient pourtant pas dans
 *    une ligne de réglage (un parc rangé par caisse ; un compte d'envoi qu'on éprouve par un test) : l'onglet les PORTE,
 *    sous ses sections. Fermée par le type, la liste oblige chaque bloc déclaré à avoir son composant.
 */
export type TabPanel = 'peripheralFleet' | 'storeMail' | 'articleArchive';

export interface OngletParam {
  key: string;
  label: Translatable;
  icon: IconName;
  note: Translatable;
  sections: SectionParam[];
  /** Le bloc rendu SOUS les sections, quand l'onglet en porte un. */
  panel?: TabPanel;
}

/** Les réglages que l'ÉDITEUR fixe : l'exploitant les voit, il ne les change pas. */
const VERROUILLES = new Set<string>([
  // La certification appartient à Orgazone : un magasin qui la modifierait imprimerait une mention
  // fausse sur chaque ticket — c'est le cas que la maquette marque « oz » en premier.
  'nf525.category', 'nf525.cert_number', 'nf525.cert_label',
  // L'identité fiscale de la caisse est FIGÉE dès la première pièce signée (§12.6) : la rendre
  // éditable inviterait à casser une chaîne en service.
  'register.gln', 'register.number', 'register.env', 'register.nf525',
  // Le radical EAN engage des codes-barres déjà imprimés.
  'store.ean_radical',
  // Le RÉGIME DE TVA et le NUMÉRO DE TVA sont décidés dans la Console à la création du client, et descendent : le régime
  // change ce que facturent toutes les caisses, le numéro se calcule du SIREN et du régime — jamais saisi (28/10).
  'company.vat_franchise', 'company.tva',
  // La devise est choisie à la création du magasin et ne change jamais (CADRAGE-Devise).
  'currency.code', 'currency.fraction', 'currency.numeric',
  // La bascule de journée est DÉRIVÉE des horaires du magasin : elle se lit ici, elle se règle là-bas.
  'fiscal.day_cutoff',
]);

/**
 * LES RÉGLAGES QUI ONT LEUR PROPRE ÉCRAN — absents d'ici, et ce n'est pas un oubli.
 *
 * ⚠️ Un réglage se règle À UN SEUL ENDROIT. Le montrer aussi ici ferait deux portes vers la même
 * valeur : celle qui a le contexte (les horaires, avec la bascule qu'ils calculent et le verrou de
 * continuité) et une case nue qui n'en sait rien. C'est la seconde qui casserait la première.
 * ⚠️ La garde `parametres-modele.test.ts` s'appuie sur cette liste : ce qui n'y est pas DOIT être
 * rangé dans un onglet, sinon il n'apparaît nulle part.
 */
export const REGLES_AILLEURS = new Set<string>([
  // Réglages → Magasin & caisses : les horaires, et la bascule qu'ils dérivent.
  'store.open', 'store.close',
  // ⚠️ 🔴 LE CALENDRIER DES ENVOIS A REJOINT LES HORAIRES, et la raison est celle écrite au-dessus :
  //    on répond à « quand ce magasin est-il ouvert ? » à UN endroit. Une case nue dans ce catalogue
  //    ne saurait ni que le fuseau commande l'heure d'un envoi, ni que les jours commandent son
  //    rattrapage — ni surtout qu’un fuseau vide BLOQUE désormais toutes les campagnes du magasin.
  'store.timezone', 'store.open_days',
  // Machinerie du verrou de continuité — jamais saisies par un humain, ni ici ni ailleurs : elles
  // sont posées par l'enregistrement des horaires et lues par tous les calculateurs.
  'fiscal.day_cutoff_pending', 'fiscal.day_cutoff_at',
  // Le groupe de mise à jour des caisses se choisit dans la CONSOLE (fiche du magasin), qui connaît les
  // groupes du store ; ici la valeur ne serait qu'un jeton illisible, et une saisie la ferait diverger
  // de ce que la Console affiche.
  'update.group_ref',
  // ⚠️ LE SENS DE SYNCHRONISATION se change dans la CONSOLE, par le super-admin SEUL (décision utilisateur) : il décide
  //    qui écrit les réglages de tout le magasin. Une case ici le rendrait modifiable par le client lui-même, et la
  //    Console, qui le réécrit à chaque re-déclaration, effacerait sa saisie.
  STORE_SYNC_MODE_SETTING,
  // ⚠️ LE CODE COMMERÇANT NEPTING se pose dans la CONSOLE, caisse par caisse, par le staff de mise en service (décision
  //    utilisateur) : il engage le contrat monétique de la caisse. Il se MONTRE sous chaque caisse dans le bloc des
  //    périphériques ; une case ici le rendrait modifiable par le client, et le serveur la refuserait.
  NEPTING_TERMINALS_SETTING,
  // ⚠️ LES PRESTATIONS DE RETOUCHE SONT DES ARTICLES (nature « prestation », fiche article — décision utilisateur) :
  //    une liste JSON en case nue ferait un second catalogue des mêmes prestations. Le réglage reste au contrat
  //    tant que des caisses d'avant le lisent ; il disparaît avec l'APK qui propose les articles de nature prestation.
  'alteration.catalog',
  // ⚠️ LES TAUX PROPOSÉS se règlent dans Référentiels › Taux de TVA : un interrupteur par taux de la loi, verrouillé quand un
  //    article, une famille ou le défaut du magasin le porte. Une case de texte ici ne saurait rien de ces verrous.
  'vat.offered_rates',
  // ⚠️ 🔴 LES RÉGLAGES D'UN CONNECTEUR VIVENT DANS SON INTÉGRATION (Intégrations → Wallet → Réglages),
  //    décision utilisateur : « ce qui concerne le wallet doit vivre dans l'intégration du
  //    wallet — un client qui n'a pas le wallet aurait ici un menu totalement inutile, tout le temps ».
  //    DÉRIVÉS du registre, jamais recopiés : une seconde liste aurait divergé au premier réglage ajouté
  //    à un connecteur, et ce réglage-là serait apparu aux deux endroits.
  ...CONNECTEURS.flatMap((c) => (c.reglages ?? []).map((r) => r.name)),
]);

/**
 * UN RÉGLAGE QUI SE RÈGLE SUR UN AUTRE ÉCRAN — donc absent des onglets, et ce n'est pas un oubli.
 *
 * ⚠️ LE VOCABULAIRE SE RECONNAÎT PAR LE CONTRAT, JAMAIS PAR UNE LISTE. Les clés `vocab.<champ>.<langue>`
 *    (le mot du commerçant, écran « Vocabulaire ») et `vocab_default.<champ>.<langue>` (le mot du métier,
 *    posé par la Console — le back-office ne l'écrit JAMAIS) sont 138 aujourd'hui, davantage au prochain
 *    champ ou à la prochaine langue. Une liste recopiée en oublierait une, et cette une-là finirait
 *    soit orpheline (invisible partout), soit rendue ici en case nue — qui laisserait un exploitant
 *    réécrire un `vocab_default` que la propagation reprend au tour suivant, sans un mot.
 */
export function hasOwnScreen(name: string): boolean {
  return REGLES_AILLEURS.has(name) || parseVocabularySettingKey(name) !== null;
}

export const gouvernanceDe =(name: string, scope: 'bo_only' | 'admin_tablet'): 'verrouille' | 'bo' | 'caisse' =>
  VERROUILLES.has(name) ? 'verrouille' : scope === 'bo_only' ? 'bo' : 'caisse';

/**
 * L'onglet contient-il au moins un réglage verrouillé ?
 *
 * ⚠️ DÉRIVÉ, jamais déclaré à côté : la maquette pose un cadenas sur ces onglets, et une liste tenue
 * à la main aurait cessé de dire la vérité au premier réglage verrouillé ajouté — un cadenas absent
 * promet un onglet entièrement modifiable, ce qui se découvre au moment de ne pas pouvoir écrire.
 */
export const ongletAVerrou = (o: OngletParam): boolean =>
  o.sections.some((s) => s.reglages.some((r) => VERROUILLES.has(r.name)));

/** Libellé et teinte d'une gouvernance — les rôles de couleur sont ceux de `setRows()`. */
export const GOUVERNANCE: Record<'verrouille' | 'bo' | 'caisse', { label: Translatable; bg: string; fg: string }> = {
  verrouille: { label: key('Verrouillé'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  bo: { label: key('Réglable ici'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  caisse: { label: key('Éditable en caisse'), bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
};

const toggle = (name: SwitchName, label: Translatable): ReglageParam => ({ name, label, forme: 'toggle' });
// ⚠️ AUCUN RÉGLAGE EN FORME DE « LISTE » N'EST RANGÉ ICI, et `ReglageParam` l'interdit : les seules listes
//    (les gabarits de coupon wallet) vivent avec leur connecteur, dans SA carte d'Intégrations.
// ⚠️ UN INTERRUPTEUR N'EST JAMAIS UNE VALEUR : le type refuse un nom de `SWITCH_DEFAULTS` ici.
const v = <N extends string>(name: N extends SwitchName ? never : N, label: Translatable, mono?: boolean): ReglageParam =>
  mono ? { name, label, forme: 'valeur', mono: true } : { name, label, forme: 'valeur' };
// ⚠️ AUCUN RÉGLAGE N'EST « secret » AUJOURD'HUI, et le raccourci a donc disparu — mais la FORME reste,
//    parce qu'elle a un emploi légitime : un secret que le SIÈGE possède et fait descendre (`bo_only`)
//    se saisit bien ici, sans jamais se relire. Ce qui a quitté cet écran est le seul secret qui n'y
//    avait pas sa place — le mot de passe SMTP, qui appartient à la TABLETTE (`device_local`, comme la
//    clé d'API) et que le siège ne reçoit donc plus. Le jour où un secret du siège arrive, on réécrit
//    ces deux lignes ; on ne garde pas un raccourci sans usage en attendant.

/**
 * Les onglets et leurs sections. Titres et descriptions de sections relevés de `SETDATA` ; les
 * réglages absents de la maquette sont rangés dans la section qui leur correspond, ou dans une
 * section ajoutée et NOMMÉE comme telle.
 */
export const ONGLETS_PARAM: readonly OngletParam[] = [
  {
    key: 'company', label: key('Société & identité'), icon: 'building',
    note: key('Ce qui vous identifie sur chaque pièce — et bloque l’encaissement tant qu’il manque.'),
    sections: [
      {
        titre: key('Identité légale'),
        note: key('Descend sur les tickets ; chaque changement est tracé au journal fiscal.'),
        reglages: [
          v('company.name', key('Raison sociale')),
          v('company.siret', 'SIRET', true),
          v('company.tva', key('TVA intracommunautaire'), true),
          v('company.naf', key('Code NAF / APE'), true),
          v('company.address', key('Adresse du siège')),
          v('company.zip', key('Code postal')),
          v('company.city', key('Ville')),
          v('company.country', key('Pays')),
          v('company.editor', key('Éditeur du logiciel')),
        ],
      },
      {
        // ⚠️ LE DESSIN DIT « en tête du ticket, dans les e-mails, sur ses pages et les bons de commande » : rien de tout
        //    cela ne les lit encore. La note dit ce qui est vrai aujourd'hui (§23.0) et change avec chaque lecteur branché.
        titre: key('Coordonnées du magasin'),
        note: key('Ce que le client verra du point de vente. La fiche Magasin les montre ; la caisse ne les imprime pas encore.'),
        reglages: [
          { name: STORE_PHONE_SETTING, label: key('Téléphone'), forme: 'telephone', mono: true },
          { name: STORE_EMAIL_SETTING, label: key('E-mail de contact'), forme: 'email' },
        ],
      },
      {
        titre: key('Régime et taux de TVA'),
        note: key('Ce que la caisse facture : le régime de la société, et le taux d’un article créé sans taux.'),
        reglages: [
          toggle('company.vat_franchise', key('Franchise en base de TVA (art. 293 B du CGI)')),
          v('vat.default_rate', key('Taux de TVA par défaut (%)')),
        ],
      },
      {
        titre: key('Certification NF525'),
        note: key('La mention ne s’imprime que si les deux champs sont remplis.'),
        reglages: [
          v('nf525.category', key('Catégorie')),
          v('nf525.cert_number', key('N° de certificat'), true),
          v('nf525.cert_label', key('Mention imprimée')),
        ],
      },
      {
        titre: key('Magasin'),
        note: key('Ce qui distingue ce point de vente des autres — le radical engage des codes déjà imprimés.'),
        reglages: [
          v('store.name', key('Nom du magasin')),
          v('store.code', key('Code magasin'), true),
          v('store.ean_radical', key('Radical EAN'), true),
        ],
      },
      {
        titre: key('Devise'),
        note: key('Choisie à la mise en service et jamais changée : l’historique est écrit dans cette unité.'),
        reglages: [
          v('currency.code', key('Code ISO'), true),
          v('currency.fraction', key('Décimales')),
          v('currency.numeric', key('Code numérique'), true),
        ],
      },
    ],
  },
  {
    key: 'sale', label: key('Vente'), icon: 'bag',
    note: key('Comment la caisse se comporte pendant la vente, avant que quiconque touche à un réglage.'),
    sections: [
      {
        titre: key('Comportement de vente'),
        note: key('Réglé ici, appliqué à toutes les caisses du magasin.'),
        reglages: [
          toggle('sale.loop', key('Vente en boucle')),
          toggle('sale.credit_note_without_client', key('Avoir sans client identifié')),
          toggle('sale.ask_client', key('Proposer un client avant d’encaisser')),
          v('sale.default_receipt', key('Justificatif par défaut')),
        ],
      },
      {
        titre: key('Remises'),
        note: key('Une remise sans motif ne se retrouve pas six mois plus tard.'),
        reglages: [
          toggle('discount.reason_required', key('Motif obligatoire')),
          // Les motifs eux-mêmes se gèrent dans Référentiels › Motifs (portée « Remises »), pas ici.
        ],
      },
      {
        titre: key('Soldes'),
        note: key('Les périodes légales — elles bornent les promotions saisonnières.'),
        reglages: [
          v('clearance.winter_dates', key('Soldes d’hiver')),
          v('clearance.summer_dates', key('Soldes d’été')),
        ],
      },
    ],
  },
  {
    key: 'receipt', label: key('Ticket & justificatif'), icon: 'receipt',
    note: key('Ce que le client emporte : mentions du ticket, justificatif proposé par défaut.'),
    sections: [
      {
        titre: key('Gabarit'),
        note: key('Ce qui s’imprime autour de la vente.'),
        reglages: [
          v('ticket.header', key('En-tête de ticket')),
          v('ticket.footer', key('Pied de ticket')),
          v('ticket.qr_link', key('Lien du QR code')),
          v('ticket.alteration_info', key('Mention retouche')),
          toggle('ticket.hide_supplier', key('Masquer la marque')),
        ],
      },
    ],
  },
  {
    key: 'pay', label: key('Moyens de paiement'), icon: 'card',
    note: key('Le terminal et ce qu’il exige — encaisser est le seul geste qu’on ne peut pas refaire plus tard.'),
    sections: [
      {
        titre: key('Terminaux de paiement'),
        note: key('Le TPE et son mode de liaison.'),
        reglages: [
          toggle('tpe.use', key('Utiliser un TPE')),
          toggle('tpe.required', key('TPE obligatoire')),
          v('tpe.cb_client', key('Ticket client CB')),
          v('tpe.cb_merchant', key('Ticket commerçant CB')),
        ],
      },
      {
        // ⚠️ CE RÉGLAGE NE CONCERNE PAS LES ACOMPTES : `deposit.notes_only` règle la REMISE EN BANQUE proposée à la
        //    clôture (le catalogue le dit, la caisse l'applique au tiroir — banques qui refusent les pièces). Rangé
        //    sous « Acomptes », il se lisait comme une règle d'encaissement qu'aucun code n'applique.
        titre: key('Remise en banque'),
        note: key('Ce que la clôture propose de sortir du tiroir pour la banque.'),
        reglages: [toggle('deposit.notes_only', key('Billets seulement — sans les pièces'))],
      },
    ],
  },
  {
    key: 'dev', label: key('Périphériques'), icon: 'printer',
    note: key('Le parc du magasin — terminaux de paiement et imprimantes — et la mécanique d’impression.'),
    panel: 'peripheralFleet',
    sections: [
      {
        titre: key('Imprimantes'),
        note: key('Réglé par caisse au besoin ; ce qui est posé ici s’applique par défaut.'),
        reglages: [
          v('print.type', key('Modèle d’imprimante')),
          v('print.cutter', key('Coupe-papier')),
          v('print.throttle', key('Débit d’impression')),
          toggle('print.bitmap', key('Impression bitmap')),
        ],
      },
    ],
  },
  {
    key: 'conf', label: key('Conformité & exercice'), icon: 'lock',
    note: key('Journée fiscale, exercice, certification : ce que l’administration lira.'),
    sections: [
      {
        titre: key('Verrous NF525'),
        note: key('Ces valeurs engagent la conformité : elles ne se règlent pas depuis un magasin.'),
        reglages: [
          v('register.gln', key('GLN du magasin'), true),
          v('register.number', key('N° de caisse'), true),
          v('register.env', key('Environnement')),
          v('register.nf525', key('Profil NF525')),
        ],
      },
      {
        titre: key('Journée fiscale'),
        note: key('L’heure à laquelle une journée bascule — elle décide de quel Z porte une vente de nuit.'),
        // ⚠️ VERROUILLÉE ICI DEPUIS LE 2026-09-07, et ce n'est pas une restriction de droits : elle
        //    est DÉRIVÉE des horaires (Réglages → Magasin & caisses). La laisser saisissable ferait
        //    deux valeurs pour le même fait — celle d'ici et celle que les horaires recalculent — qui
        //    divergeraient au premier oubli, sans que l'écart se voie nulle part.
        reglages: [v('fiscal.day_cutoff', key('Bascule de journée (calculée des horaires)'))],
      },
      {
        titre: key('Exercice comptable'),
        note: key('Les bornes de l’exercice, et son mode exceptionnel.'),
        reglages: [
          v('fiscal.closing_day', key('Jour de clôture')),
          v('fiscal.closing_month', key('Mois de clôture')),
          toggle('fiscal.ex_on', key('Exercice exceptionnel')),
          v('fiscal.ex_start', key('Début d’exercice')),
          v('fiscal.ex_end', key('Fin d’exercice')),
        ],
      },
    ],
  },
  {
    key: 'valid', label: key('Validités des titres'), icon: 'ticketTag',
    note: key('Combien de temps un avoir, un chèque cadeau ou un acompte reste utilisable.'),
    sections: [
      {
        titre: key('Durées de validité'),
        note: key('Exprimées en mois. Un titre expiré reste visible — il ne s’applique plus.'),
        reglages: [
          v('validity.credit_note', key('Avoir')),
          v('validity.gift', key('Chèque cadeau')),
          v('validity.loyalty', key('Bon de fidélité')),
          v('validity.deposit', key('Acompte')),
        ],
      },
    ],
  },
  {
    key: 'mail', label: key('Mail & SMTP'), icon: 'mail',
    note: key('Ce que le magasin envoie en son nom — ticket par courriel, messages.'),
    panel: 'storeMail',
    sections: [
      {
        titre: key('Envoi des tickets'),
        note: key('Le compte d’envoi du magasin, avec ses propres identifiants.'),
        reglages: [
          v('mail.host', key('Serveur SMTP'), true),
          v('mail.port', key('Port'), true),
          v('mail.user', key('Identifiant'), true),
          // ⚠️ 🔴 LE MOT DE PASSE SMTP N'EST PLUS PILOTABLE D'ICI, et c'est voulu. Il était rangé
          //    parmi les réglages du magasin, donc SYNCHRONISÉ : il partait en clair de la caisse,
          //    arrivait dans cette base, et redescendait sur toutes les caisses du magasin. Il est
          //    désormais `device_local` — il ne quitte jamais la tablette qui s'en sert, comme la clé
          //    d'API. Le siège ne peut donc plus l'atteindre, et cet écran cesse de prétendre le faire.
          //    ⚠️ LE RESTE DU COMPTE RESTE PILOTABLE (serveur, port, identifiant, expéditeur) : seul le
          //    SECRET est local. C'est la même ligne de partage que pour la clé d'API.
          toggle('mail.ssl', key('Connexion SSL')),
          toggle('mail.tls', key('Passer en TLS après connexion')),
          v('mail.from', key('Adresse d’expédition'), true),
          v('mail.subject', key('Objet du message')),
          v('mail.body', key('Corps du message')),
          toggle('mail.pro', key('Compte professionnel')),
          v('mail.credit', key('Crédits restants')),
        ],
      },
      {
        titre: 'SMS',
        note: key('L’expéditeur affiché sur le téléphone du client.'),
        reglages: [
          v('sms.sender', key('Nom d’expéditeur')),
          v('sms.message_type', key('Type de message')),
        ],
      },
    ],
  },
  {
    key: 'sync', label: key('Synchronisation & sécurité'), icon: 'refresh',
    note: key('Le rythme de synchronisation, la règle hors ligne et le verrouillage de session.'),
    sections: [
      {
        titre: key('Synchronisation'),
        note: key('À quelle cadence la caisse parle au back-office.'),
        reglages: [
          toggle('api.sync_auto', key('Synchronisation automatique')),
          v('api.delay', key('Intervalle (secondes)')),
        ],
      },
      {
        titre: key('Travail hors ligne'),
        note: key('Jusqu’où une caisse peut encaisser sans avoir parlé au back-office.'),
        reglages: [
          toggle('api.offline_rule', key('Règle appliquée')),
          // ⚠️ « Bloquer au-delà du délai » DÉCRIVAIT LE TRAVAIL DE `api.offline_rule`, pas le sien : deux
          //    écrans disaient deux choses du même réglage. Celui-ci parle du hors-ligne IMMÉDIAT — et la
          //    note du catalogue, rendue sous le libellé, dit qu'il n'est pas appliqué aujourd'hui.
          toggle('api.block_offline', key('Bloquer l’encaissement hors ligne')),
          v('api.offline_warn_days', key('Avertir après (jours)')),
          v('api.offline_lock_days', key('Bloquer après (jours)')),
        ],
      },
      {
        titre: key('Sécurité'),
        note: key('Ce qui protège la caisse quand personne ne la regarde.'),
        reglages: [v('app.lock_min', key('Verrouillage après (minutes)'))],
      },
    ],
  },
  {
    // ⚠️ AUCUN RÉGLAGE, UN GESTE : l'archivage n'est pas une valeur qu'on pose, c'est une opération sur le catalogue
    //    (CADRAGE-Archivage-Articles.md). L'onglet ne porte donc que son bloc — le dessin le range ici, en dernier.
    key: 'arch', label: key('Archivage des articles'), icon: 'lidBox',
    note: key('Sortir les vieux modèles des recherches sans toucher à l’histoire des ventes.'),
    panel: 'articleArchive',
    sections: [],
  },
];

/** Tous les réglages rangés, à plat — sert aux gardes et à la recherche globale. */
export const REGLAGES_RANGES: readonly { reglage: ReglageParam; onglet: OngletParam; section: SectionParam }[] =
  ONGLETS_PARAM.flatMap((onglet) =>
    onglet.sections.flatMap((section) => section.reglages.map((reglage) => ({ reglage, onglet, section }))));

/** L'emplacement d'un réglage, ou `null` s'il n'est rangé nulle part (ce que la garde interdit). */
export function placeDe(name: string) {
  return REGLAGES_RANGES.find((x) => x.reglage.name === name) ?? null;
}
